import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractAsmService } from '@asm/components-core/services/abstract-asm.service';
import { AsmLicensingAuthorizationTools } from '@asm/components-core/tools/asm-licensing-authorization.tools';
import { SolAuthority } from '@sol/components';
import { Observable, filter, map, takeUntil } from 'rxjs';

interface LicensingAuthoritiesDto {
  permissions: string[];
}

interface LicensingAuthorizationState {
  authorities: SolAuthority[];
  fetched: boolean;
  token: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class LicensingAuthorizationService extends AbstractAsmService<LicensingAuthorizationState> {
  // Dependencies
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super('/api/v3', { authorities: [], fetched: false, token: undefined });
    const tokens = this.compute(state => state.token);
    this.authenticationService.watchComputed(
      ({ token, fetched, enabled }) => ({ token, fetched, enabled }),
      ({ token, fetched, enabled }) => {
        const authorities = AsmLicensingAuthorizationTools.toAuthorities(
          token,
          this.asmComponentsService.state.environment,
        );
        this.patch({
          token,
          authorities,
          fetched: !enabled || (fetched && (authorities.length > 0 || !token)),
        });
        if (!enabled || !fetched || !token) {
          return;
        }
        this.fetchAuthorities()
          .pipe(takeUntil(tokens.pipe(filter(current => current !== token))), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: backendAuthorities => this.completeAuthorization(backendAuthorities),
            error: () => this.completeAuthorization([]),
          });
      },
    );
  }

  protected override onUnauthorized(): void {
    this.completeAuthorization([]);
  }

  private fetchAuthorities(): Observable<SolAuthority[]> {
    return this.httpGet<LicensingAuthoritiesDto>('/authorities').pipe(
      map(dto => AsmLicensingAuthorizationTools.permissionsToAuthorities(dto.permissions.map(p => p.toUpperCase()))),
    );
  }

  private completeAuthorization(backendAuthorities: SolAuthority[]): void {
    const tokenAuthorities = AsmLicensingAuthorizationTools.toAuthorities(
      this.state.token,
      this.asmComponentsService.state.environment,
    );
    this.patch({ authorities: [...new Set([...tokenAuthorities, ...backendAuthorities])], fetched: true });
  }
}
